/**
 * Session state for the tree, plus the API clients every page shares.
 *
 * One provider, and a client per credential: the client carries the bearer token getter, so a page that
 * wants data asks the context for the client and cannot accidentally construct an unauthenticated one.
 *
 * TWO SESSIONS, BECAUSE ONE PERSON CAN BE TWO THINGS AT ONCE (owner feedback: a staff sign-in opened
 * Stats and was told "this page is for a player session"). {@link SessionContextValue.session} is the
 * SIGN-IN — Discord's, or the one an `/audio` claim minted — and it is what the staff console runs on.
 * {@link SessionContextValue.self} is WHO THIS BROWSER PLAYS AS: a `player`-tier token for the reader's
 * own rows, which is what Stats reads with. For a player the two are one token; for a member of staff
 * who also plays they are two, and keeping them apart is the point — a staff token never names a player
 * (`isEditorJwtClaims` refuses it), and a player token never becomes a console.
 *
 * `self` OUTLIVES THE SIGN-IN. It is re-minted from the renewal cookie the `/audio` claim (or a sign-in
 * whose Discord account `/link` bound) left on this device — thirty days, sliding — so closing the app,
 * a sidecar restart or a game-server reboot no longer signs anybody out of their own stats. The sign-in
 * itself still lives fifteen minutes in this tab's `sessionStorage`, as before.
 */
import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { isStaff } from '@dimensionscore/shared';

import { ApiClient } from '../api/client';
import { clearRenewalHint, hasRenewalHint, writePlayerHint, writeRenewalHint } from '../audio/prefs';
import { AudioSessionClient, type SessionOutcome } from '../audio/session';
import {
  clearStoredSession,
  describeAuthError,
  loginUrl,
  parseAuthFragment,
  readStoredSession,
  sessionFromToken,
  storeSession,
  type Session,
} from './session';

export interface SessionContextValue {
  /** The sign-in: Discord's (staff, player or unlinked), or the player session an `/audio` claim minted. */
  readonly session: Session | null;
  /**
   * Who this browser plays as — a `player`-tier session for the reader's own rows — or null when this
   * device does not know. The sign-in itself when that is a player's; otherwise re-minted from the
   * renewal (see the file header). Stats reads with this, through {@link selfClient}.
   */
  readonly self: Session | null;
  /** True while the provider is still asking the renewal who this browser plays as. */
  readonly selfChecking: boolean;
  /** True when that question could not be answered just now (the sidecar or the binding lookup was down). */
  readonly selfUnavailable: boolean;
  /** The client whose bearer is {@link self}'s token: the one a page about the reader's own rows uses. */
  readonly selfClient: ApiClient;
  /** Asks the renewal again — after a `/link`, say, or from a "check again" button. */
  readonly refreshSelf: () => void;
  /** Takes a player session the renewal minted (the audio companion hands over every one it is given). */
  readonly adoptSelf: (token: string) => void;
  /** Last sign-in failure, already worded for a human. Cleared by a new attempt. */
  readonly authError: string | null;
  readonly client: ApiClient;
  readonly signIn: (returnTo: string) => void;
  /** Signs out of the SPA AND clears this browser's audio renewal (plan §9.2.4: "signing out clears it"). */
  readonly signOut: () => void;
  /**
   * How many times {@link signOut} has run in this tab. The audio page watches it to stop playing on a
   * sign-out from the header, which a session simply EXPIRING must not do — an expired token is the
   * renewal's whole reason to exist, so `session` going null cannot be the signal.
   */
  readonly signOuts: number;
  /**
   * Takes a player-tier session the audio claim minted (plan §9.2.3). A STAFF session already in the tab
   * is kept: the claim proves who the person plays as, not that their console should become a player's —
   * so then the token becomes {@link self} instead. Returns whether it became the sign-in.
   */
  readonly adoptSession: (token: string) => boolean;
  /** The one client for the audio way in, built on the same API base as {@link client}. */
  readonly audio: AudioSessionClient;
}

const SessionContext = createContext<SessionContextValue | null>(null);

/**
 * Where the sidecar lives. Empty (`npm run build:same-origin`) means same-origin: `/v1` routed beside the
 * SPA. The Rotom Phone's hosting (plan §9.11) is a same-site API subdomain, which every plain `vite build`
 * bakes in from `app/.env.production`: `VITE_API_BASE=https://api.cobblemondimensions.com`, the page itself
 * on `phone.cobblemondimensions.com`.
 */
export const API_BASE: string = import.meta.env['VITE_API_BASE'] ?? '';

/** How long before `self` lapses it is re-minted: a minute, well inside its fifteen. */
export const SELF_REFRESH_LEAD_MS = 60_000;

/** …but never sooner than this after the last answer, whatever this browser's clock says. */
export const SELF_REFRESH_MIN_MS = 30_000;

export function SessionProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const [session, setSession] = useState<Session | null>(() => readStoredSession(globalThis.sessionStorage));
  const [authError, setAuthError] = useState<string | null>(null);

  /*
   * Consume the `#dc_token=...` the Worker redirected home with, then strip the fragment
   * from the address bar with replaceState - so the credential is not in the visible URL,
   * not in the back stack, and not in a screenshot.
   *
   * Runs once. React 18/19 StrictMode double-invokes effects in development; the second
   * pass finds an empty hash and does nothing, which is why the fragment is read from the
   * live location rather than from a captured value.
   *
   * A LAYOUT EFFECT, AND THE TREE WAITS FOR IT. A sign-in returns to the page it started on, and one
   * of those - `/`, which the audio page's staff door returns to - is a route that redirects a
   * signed-out visitor on to `/audio`. Rendered before the fragment was read, that route decides
   * "signed out", and its redirect is a `replace` in an effect: children's effects run before their
   * parents', so the address was rewritten, fragment and credential with it, before this provider
   * ever looked. So while the address carries a sign-in fragment the provider renders no children,
   * reads the fragment in a layout effect - before paint, so the empty pass is never seen - and only
   * then renders the routes, with the session already known.
   */
  const [fragmentRead, setFragmentRead] = useState(() => parseAuthFragment(globalThis.location.hash) === null);
  useLayoutEffect(() => {
    const fragment = parseAuthFragment(globalThis.location.hash);
    setFragmentRead(true);
    if (fragment === null) return;

    globalThis.history.replaceState(null, '', `${globalThis.location.pathname}${globalThis.location.search}`);

    if (fragment.error !== null) {
      setAuthError(describeAuthError(fragment.error));
      return;
    }
    if (fragment.token === null) return;

    const parsed = sessionFromToken(fragment.token);
    if (parsed === null) {
      setAuthError('The sign-in token was malformed or already expired.');
      return;
    }
    storeSession(globalThis.sessionStorage, fragment.token);
    setAuthError(null);
    setSession(parsed);
  }, []);

  /*
   * Sign out by ourselves the moment the token expires, rather than letting the pages
   * discover it as a wall of 401s. The timer is exact because `exp` is in the claims.
   *
   * A PLAYER'S SIGN-IN LAPSING IS NOT A SIGN-OUT WHEN THE RENEWAL CARRIES ON. Its only job — the
   * reader's own rows — is `self`'s, and `self` is re-minted from the renewal, so saying "your session
   * expired, sign in again" to somebody whose Stats are still open and still fresh would be false. Only
   * a staff (or unlinked) sign-in lapsing is worth a word: that is the console going away.
   */
  const renewedSelfRef = useRef<Session | null>(null);
  useEffect(() => {
    if (session === null) return;
    const millisecondsLeft = session.claims.exp * 1000 - Date.now();
    const timer = setTimeout(() => {
      clearStoredSession(globalThis.sessionStorage);
      setSession(null);
      const carriedOn = session.claims.role === 'player' && (renewedSelfRef.current !== null || hasRenewalHint());
      if (!carriedOn) setAuthError('Your session expired. Sign in again.');
    }, Math.max(0, millisecondsLeft));
    return () => clearTimeout(timer);
  }, [session]);

  const client = useMemo(() => new ApiClient({ baseUrl: API_BASE, getToken: () => session?.token ?? null }), [session]);

  const signIn = useCallback((returnTo: string) => {
    setAuthError(null);
    globalThis.location.assign(loginUrl(API_BASE, returnTo));
  }, []);

  const audio = useMemo(() => new AudioSessionClient({ apiBase: API_BASE }), []);

  // ------------------------------------------------------------------ who this browser plays as
  const [renewedSelf, setRenewedSelf] = useState<Session | null>(null);
  renewedSelfRef.current = renewedSelf;
  const [selfChecking, setSelfChecking] = useState(false);
  const [selfUnavailable, setSelfUnavailable] = useState(false);
  /*
   * The renewal's token wins over a player sign-in's for the same reason it is minted: it is the fresh
   * one. A player sign-in with no renewal behind it yet still counts — it names the same player, and the
   * first renewal answer replaces it.
   */
  const self = renewedSelf ?? (session !== null && session.claims.role === 'player' ? session : null);
  const sessionRef = useRef(session);
  sessionRef.current = session;

  const adoptSelf = useCallback((token: string): void => {
    const parsed = sessionFromToken(token);
    if (parsed === null || parsed.claims.role !== 'player') return;
    setRenewedSelf(parsed);
    setSelfUnavailable(false);
  }, []);

  /** What the renewal answered, applied: the hints the companion reads, and the player session. */
  const applyRenewal = useCallback(
    (outcome: SessionOutcome): void => {
      if (outcome.ok) {
        writeRenewalHint(outcome.issued.renewalExpiresAt);
        writePlayerHint(outcome.issued.player);
        // A sidecar older than the field answers without it: then there is simply no `self` to show.
        if (typeof outcome.issued.token === 'string') adoptSelf(outcome.issued.token);
        return;
      }
      if (outcome.reason === 'none') {
        setRenewedSelf(null);
        setSelfUnavailable(false);
        return;
      }
      setSelfUnavailable(true);
    },
    [adoptSelf],
  );

  /*
   * ASK THE RENEWAL, with the sign-in as the fallback credential: the cookie answers first when this
   * device has one, and otherwise a sign-in that names a player (a linked Discord account, staff
   * included) establishes it. One request per question; the answer is shared with nobody else's state.
   */
  const askRenewal = useCallback(async (): Promise<void> => {
    setSelfChecking(true);
    try {
      applyRenewal(await audio.session(sessionRef.current?.token ?? null));
    } finally {
      setSelfChecking(false);
    }
  }, [audio, applyRenewal]);

  /*
   * WHEN TO ASK: whenever the sign-in changes and something could answer — a renewal this device
   * probably holds, or a sign-in that can name a player: a staff one (through its Discord account's
   * binding) or a player's own. A player's sign-in is asked about although it already names its
   * player, because asking is what leaves the renewal behind, so a linked player who only ever opens
   * Stats is remembered past their fifteen minutes exactly as a listener is. An `unlinked` sign-in
   * names nobody by definition, so on its own it is not worth a request.
   */
  const sessionToken = session?.token ?? null;
  const sessionRole = session?.claims.role ?? null;
  useEffect(() => {
    const couldAnswer = hasRenewalHint() || (sessionRole !== null && sessionRole !== 'unlinked');
    if (!couldAnswer) return;
    void askRenewal();
  }, [sessionToken, sessionRole, askRenewal]);

  /*
   * AND AGAIN A MINUTE BEFORE `self` LAPSES, so a Stats page left open keeps reading. Tab-driven like the
   * expiry timer above; a sleeping laptop wakes to one late refresh, and the polled views ask again on
   * focus.
   */
  useEffect(() => {
    if (self === null || !hasRenewalHint()) return;
    const millisecondsLeft = self.claims.exp * 1000 - Date.now() - SELF_REFRESH_LEAD_MS;
    // Never sooner than the floor: a browser clock running fast would otherwise see every fresh token as
    // already lapsing and re-ask in a loop, while the sidecar (whose clock signs it) holds it good.
    const timer = setTimeout(() => void askRenewal(), Math.max(SELF_REFRESH_MIN_MS, millisecondsLeft));
    return () => clearTimeout(timer);
  }, [self, askRenewal]);

  const refreshSelf = useCallback(() => void askRenewal(), [askRenewal]);
  const selfClient = useMemo(() => new ApiClient({ baseUrl: API_BASE, getToken: () => self?.token ?? null }), [self]);

  const [signOuts, setSignOuts] = useState(0);
  const signOut = useCallback(() => {
    clearStoredSession(globalThis.sessionStorage);
    setSession(null);
    setRenewedSelf(null);
    setSelfUnavailable(false);
    setAuthError(null);
    clearRenewalHint();
    void audio.signOut();
    setSignOuts((count) => count + 1);
  }, [audio]);

  const adoptSession = useCallback(
    (token: string): boolean => {
      const parsed = sessionFromToken(token);
      if (parsed === null) return false;
      const held = readStoredSession(globalThis.sessionStorage);
      if (held !== null && isStaff(held.claims.role)) {
        // The console stays; who this person plays as is still worth knowing — that is `self`.
        adoptSelf(token);
        return false;
      }
      storeSession(globalThis.sessionStorage, token);
      setAuthError(null);
      setSession(parsed);
      return true;
    },
    [adoptSelf],
  );

  const value = useMemo<SessionContextValue>(
    () => ({
      session,
      self,
      selfChecking,
      selfUnavailable,
      selfClient,
      refreshSelf,
      adoptSelf,
      authError,
      client,
      signIn,
      signOut,
      signOuts,
      adoptSession,
      audio,
    }),
    [session, self, selfChecking, selfUnavailable, selfClient, refreshSelf, adoptSelf, authError, client, signIn, signOut, signOuts, adoptSession, audio],
  );

  return <SessionContext.Provider value={value}>{fragmentRead ? children : null}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const value = useContext(SessionContext);
  if (value === null) throw new Error('useSession must be used inside a <SessionProvider>');
  return value;
}
